export const AOX_NAMES = ['ΑΟ ΧΑΝΙΑ', 'Α.Ο. ΧΑΝΙΑ', 'ΑΟΧ', 'Α.Ο.Χ', 'Α.Ο. ΧΑΝΙΑ Κ17', 'ΑΟ ΧΑΝΙΑ Κ17'];

export const DEFAULT_MATCHDAY_SETTINGS = {
  enabled: true,
  windowDays: 5,
  matchdayHours: 24,
  liveFallbackHours: 3,
  upcomingCount: 2,
  selectionMode: 'automatic',
  manualMatchId: null,
};

const clampNumber = (value, min, max, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
};

export const normalizeMatchdaySettings = (config = {}) => ({
  enabled: config.matchday_mode_enabled !== false,
  windowDays: clampNumber(config.matchday_window_days, 0, 30, DEFAULT_MATCHDAY_SETTINGS.windowDays),
  matchdayHours: clampNumber(config.matchday_threshold_hours, 1, 72, DEFAULT_MATCHDAY_SETTINGS.matchdayHours),
  liveFallbackHours: clampNumber(config.matchday_live_fallback_hours, 1, 12, DEFAULT_MATCHDAY_SETTINGS.liveFallbackHours),
  upcomingCount: clampNumber(config.matchday_upcoming_count, 1, 3, DEFAULT_MATCHDAY_SETTINGS.upcomingCount),
  selectionMode: ['automatic', 'featured', 'manual'].includes(config.matchday_selection_mode)
    ? config.matchday_selection_mode
    : DEFAULT_MATCHDAY_SETTINGS.selectionMode,
  manualMatchId: config.matchday_manual_match_id || null,
});

export const isAoxTeam = (name = '') => {
  const normalized = String(name).trim().toUpperCase();
  return AOX_NAMES.some((team) => normalized.includes(team));
};

export const matchDateTime = (match) => {
  if (!match?.date) return null;
  const date = String(match.date).slice(0, 10);
  const time = match.time ? String(match.time).slice(0, 8) : '15:00:00';
  const parsed = new Date(`${date}T${time}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getMatchPhase = (match, now = new Date(), rawSettings = {}) => {
  if (!match) return 'normal';
  const settings = { ...DEFAULT_MATCHDAY_SETTINGS, ...rawSettings };

  if (match.status === 'live') return 'live';
  if (match.status === 'completed') return 'finished';
  if (['canceled', 'postponed'].includes(match.status)) return match.status;

  const kickoff = matchDateTime(match);
  if (!kickoff) return 'normal';

  const diff = kickoff.getTime() - now.getTime();
  const hour = 60 * 60 * 1000;
  const day = 24 * hour;

  if (diff <= 0 && diff > -(settings.liveFallbackHours * hour)) return 'live';
  if (diff > 0 && diff <= settings.matchdayHours * hour) return 'matchday';
  if (diff > settings.matchdayHours * hour && diff <= settings.windowDays * day) return 'soon';
  return 'normal';
};

const activeRows = (matches = []) => (matches || [])
  .filter((match) => !['canceled', 'postponed'].includes(match.status))
  .map((match) => ({ match, kickoff: matchDateTime(match) }))
  .filter(({ kickoff }) => kickoff)
  .sort((a, b) => a.kickoff - b.kickoff);

export const selectMatchdayMatches = (matches = [], now = new Date(), rawSettings = {}) => {
  const settings = { ...DEFAULT_MATCHDAY_SETTINGS, ...rawSettings };
  if (!settings.enabled) return { primary: null, following: [] };

  const rows = activeRows(matches);
  const liveWindowMs = settings.liveFallbackHours * 60 * 60 * 1000;
  const windowMs = settings.windowDays * 24 * 60 * 60 * 1000;

  const live = rows.find(({ match, kickoff }) => {
    if (match.status === 'live') return true;
    const diff = now.getTime() - kickoff.getTime();
    return match.status !== 'completed' && diff >= 0 && diff <= liveWindowMs;
  });

  const eligibleUpcoming = rows.filter(({ match, kickoff }) => {
    if (match.status === 'completed') return false;
    const diff = kickoff.getTime() - now.getTime();
    return diff >= 0 && diff <= windowMs;
  });

  let primaryRow = live || null;

  if (!primaryRow && settings.selectionMode === 'manual' && settings.manualMatchId) {
    primaryRow = eligibleUpcoming.find(({ match }) => match.id === settings.manualMatchId) || null;
  }

  if (!primaryRow && settings.selectionMode === 'featured') {
    primaryRow = eligibleUpcoming.find(({ match }) => match.is_featured) || null;
  }

  if (!primaryRow) {
    primaryRow = eligibleUpcoming[0] || null;
  }

  if (!primaryRow) return { primary: null, following: [] };

  const primary = primaryRow.match;
  const following = eligibleUpcoming
    .filter(({ match }) => match.id !== primary.id)
    .slice(0, Math.max(0, settings.upcomingCount - 1))
    .map(({ match }) => match);

  return { primary, following };
};

export const findMatchdayMatch = (matches = [], now = new Date(), rawSettings = {}) =>
  selectMatchdayMatches(matches, now, rawSettings).primary;

export const formatMatchDate = (match) => {
  const kickoff = matchDateTime(match);
  if (!kickoff) return '';
  return kickoff.toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long' });
};

export const formatMatchTime = (match) => match?.time ? String(match.time).slice(0, 5) : '';

export const getOpponentName = (match) => {
  if (!match) return '';
  return isAoxTeam(match.home_team) ? match.away_team : match.home_team;
};
